import type { PropsWithChildren } from 'react';
import { ConfigProvider, App as AntApp } from 'antd';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { BrowserRouter } from 'react-router-dom';
import { queryClient } from './queryClient';
import { antdThemeTokens } from '@/theme/tokens';
import '@/i18n'; // side-effect import: initializes i18next once, at app startup

/**
 * providers.tsx - every app-wide context provider, in one place, in the
 * order that matters (Router outside ConfigProvider outside QueryClient -
 * none of these actually depend on ordering here, but keeping them
 * together means App.tsx itself stays about ROUTES, not setup).
 *
 * <AntApp> (not <App> the component we're writing - Ant Design's own
 * App component) is what makes `message`/`notification`/`Modal` static
 * methods pick up the ConfigProvider theme instead of rendering with
 * Ant's un-themed defaults - easy to miss and a common source of "why
 * doesn't my success message use my brand colour" bugs.
 */
export function AppProviders({ children }: PropsWithChildren) {
  return (
    <BrowserRouter>
      <QueryClientProvider client={queryClient}>
        <ConfigProvider theme={{ token: antdThemeTokens }} form={{ colon: false }}>
          <AntApp>{children}</AntApp>
        </ConfigProvider>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </BrowserRouter>
  );
}
